// Hebrew Pack — the files under "skills projects/" served to the management page.
//
// WHY A GLOB AND NOT A FETCH
//   The pack is the source of truth and it lives in the repository, not in the
//   database. Reading it at build time means the page can never drift from what
//   is actually on disk: change a rule, the page shows the new rule on the next
//   build. Nothing to sync, nothing to seed.
//
//   That claim was false until 2026-09-07: `skills projects/` was in .gitignore,
//   so CI checked out a tree without it, this glob matched nothing, and the page
//   shipped empty to production while every local build worked. The directory is
//   committed now. If it is ever excluded again, this file silently returns [].

const raw = import.meta.glob<string>('/skills projects/**/*.{md,json}', {
  query: '?raw',
  import: 'default',
  eager: true,
});

export type PackGroup = 'התחלה' | 'פרומפטים' | 'כללים' | 'סוכן' | 'סקילים';

export interface PackFile {
  /** Path relative to the pack root, e.g. "rules/01-core.md". */
  path: string;
  name: string;
  group: PackGroup;
  body: string;
  bytes: number;
}

const PREFIX = '/skills projects/';

function groupOf(path: string): PackGroup {
  if (path.startsWith('prompts/')) return 'פרומפטים';
  if (path.startsWith('rules/')) return 'כללים';
  if (path.startsWith('agents/')) return 'סוכן';
  if (path.startsWith('skills/')) return 'סקילים';
  return 'התחלה';
}

/** Fixed display order. Anything unlisted sorts last, alphabetically. */
export const GROUP_ORDER: PackGroup[] = ['התחלה', 'פרומפטים', 'כללים', 'סוכן', 'סקילים'];

export const PACK_FILES: PackFile[] = Object.entries(raw)
  .map(([absolute, body]) => {
    const path = absolute.startsWith(PREFIX) ? absolute.slice(PREFIX.length) : absolute;
    return {
      path,
      name: path.split('/').pop() ?? path,
      group: groupOf(path),
      body,
      bytes: new TextEncoder().encode(body).length,
    };
  })
  .sort((a, b) => {
    const ga = GROUP_ORDER.indexOf(a.group);
    const gb = GROUP_ORDER.indexOf(b.group);
    if (ga !== gb) return (ga < 0 ? 99 : ga) - (gb < 0 ? 99 : gb);
    return a.path.localeCompare(b.path);
  });

export function filesInGroup(group: PackGroup): PackFile[] {
  return PACK_FILES.filter((f) => f.group === group);
}

/** One markdown document holding the whole pack, for a single download. */
export function bundle(): string {
  const head = [
    '# Hebrew Pack',
    '',
    `נוצר מתוך ${String(PACK_FILES.length)} קבצים.`,
    'כל קובץ מופרד בכותרת עם הנתיב שלו.',
    '',
    '---',
    '',
  ].join('\n');
  return (
    head +
    PACK_FILES.map((f) => `\n\n<!-- ===== ${f.path} ===== -->\n\n${f.body}`).join('\n\n---\n')
  );
}
